/**
 * Lógica pura del multi-scan del BarcodeScannerModal.
 * Extraída para ser testeable sin mockear expo-camera/React.
 */

export type MultiScanState = {
  pendingReads: string[];
  mismatchCount: number;
};

export type MultiScanResult =
  | { kind: 'pending'; state: MultiScanState }
  | { kind: 'accepted'; code: string; state: MultiScanState }
  | { kind: 'fallback'; code: string; state: MultiScanState };

export const MAX_MISMATCHES = 3;

/**
 * Procesa una lectura del scanner y decide:
 * - `pending`: todavía no hay dos lecturas consecutivas para comparar
 * - `accepted`: dos lecturas consecutivas coinciden → aceptar el código
 * - `fallback`: se alcanzó el máximo de mismatches → mostrar el último código
 */
export function processScanRead(
  current: MultiScanState,
  code: string,
  maxMismatches: number = MAX_MISMATCHES,
): MultiScanResult {
  const reads = [...current.pendingReads, code];

  if (reads.length < 2) {
    return { kind: 'pending', state: { pendingReads: reads, mismatchCount: current.mismatchCount } };
  }

  const last = reads[reads.length - 1];
  const prev = reads[reads.length - 2];

  if (last === prev) {
    return {
      kind: 'accepted',
      code: last,
      state: { pendingReads: [], mismatchCount: 0 },
    };
  }

  const nextMismatch = current.mismatchCount + 1;
  if (nextMismatch >= maxMismatches) {
    return {
      kind: 'fallback',
      code: last,
      state: { pendingReads: [], mismatchCount: nextMismatch },
    };
  }

  return {
    kind: 'pending',
    state: { pendingReads: [code], mismatchCount: nextMismatch },
  };
}
